import { z } from 'zod';

export const MAX_TRIP_MESSAGE_LENGTH = 2000;
export const MAX_TRIP_MESSAGE_PHOTOS = 4;

const tripMessageBodySchema = z.string().trim().max(MAX_TRIP_MESSAGE_LENGTH);

/** A message is words, photos, or both: an empty one is never stored. */
function hasContent(message: { body: string; mediaClientIds: readonly string[] }): boolean {
  return message.body.length > 0 || message.mediaClientIds.length > 0;
}

/**
 * `POST /trips/:id/messages` (the office, gitaumoses4/chukua#106). The driver is the trip's current one, never named by the caller. `mediaClientIds`
 * are the ids the photos were confirmed under (`media/confirm`), which is how the thread finds them once they are uploaded.
 */
export const sendTripMessageSchema = z
  .object({
    body: tripMessageBodySchema.default(''),
    mediaClientIds: z.array(z.uuid()).max(MAX_TRIP_MESSAGE_PHOTOS).default([]),
    /** The browser's id for this send: a double click or a retry lands on one message. */
    clientMessageId: z.uuid().optional(),
  })
  .refine(hasContent, { message: 'A message needs text or a photo', path: ['body'] });
export type SendTripMessageInput = z.infer<typeof sendTripMessageSchema>;

/** `POST /sync/trips/:tripId/messages` (the phone). The phone's id is required: it is the idempotency anchor of an offline-queued reply. */
export const syncTripMessageRequestSchema = z
  .object({
    body: tripMessageBodySchema.default(''),
    mediaClientIds: z.array(z.uuid()).max(MAX_TRIP_MESSAGE_PHOTOS).default([]),
    clientMessageId: z.uuid(),
    deviceTime: z.iso.datetime({ offset: true }),
  })
  .refine(hasContent, { message: 'A message needs text or a photo', path: ['body'] });
export type SyncTripMessageRequest = z.infer<typeof syncTripMessageRequestSchema>;

export const syncTripMessageResponseSchema = z.object({
  messageId: z.uuid(),
  /** True when this `clientMessageId` was stored before, so nothing was written now. */
  alreadyRecorded: z.boolean(),
});
export type SyncTripMessageResponse = z.infer<typeof syncTripMessageResponseSchema>;

/** `GET /sync/trips/:tripId/messages?after=<message id>`: the messages written after that one. Without `after`, the whole thread. */
export const tripMessageCursorQuerySchema = z.object({ after: z.uuid().optional() });
export type TripMessageCursorQuery = z.infer<typeof tripMessageCursorQuerySchema>;

/**
 * `PUT /trips/:id/messages/read` (the office). `upToMessageId` is the newest message the screen actually showed, so a driver message that arrived
 * after the thread was fetched stays unread instead of being cleared unseen.
 */
export const markTripMessagesReadSchema = z.object({ upToMessageId: z.uuid() });
export type MarkTripMessagesReadInput = z.infer<typeof markTripMessagesReadSchema>;

export const TRIP_MESSAGE_AUTHOR_KINDS = ['office', 'driver'] as const;
export type TripMessageAuthorKind = (typeof TRIP_MESSAGE_AUTHOR_KINDS)[number];

/** `url` is null while the photo is not in storage yet (the message can arrive before its photos do). */
export interface TripMessagePhotoView {
  clientMediaId: string;
  url: string | null;
}

export interface TripMessageView {
  id: string;
  tripId: string;
  authorKind: TripMessageAuthorKind;
  /** The office user's or the driver's name; null when the account is gone. */
  authorName: string | null;
  body: string;
  photos: TripMessagePhotoView[];
  /** The server's clock: the order of the thread. */
  createdAt: string;
  /** The phone's own id, so it can match the row it queued to the one the server stored. */
  clientMessageId: string | null;
}

export interface TripMessageThreadView {
  messages: TripMessageView[];
}

/** Driver messages the signed-in user has not opened yet, per trip. A trip with none is absent. */
export interface TripMessageUnreadView {
  byTrip: Record<string, number>;
}
